import {
  DEFAULT_LANGUAGE,
  LIVE_LANGUAGES,
  isSupportedLanguage,
} from '../i18n/supported-languages.ts'
import { isPublicPageHidden } from './hidden-public-pages.ts'
import type { SupportedLanguage } from '../i18n/supported-languages.ts'

/**
 * Canonical public origin. Kept as a constant (not env-derived) so prerender,
 * sitemap, canonical and hreflang stay deterministic regardless of build args.
 */
export const SITE_URL = 'https://laplace-gtm.com'
/** Link preview; regenerate with `pnpm --filter @laplace/chat og:render`. */
export const OG_IMAGE = `${SITE_URL}/og-gtm-2026-10.jpg`
export const SITE_NAME = 'Laplace x GTM'
/** The product the public site sells; brand searches are for this name. */
export const PRODUCT_NAME = 'Laplace x GTM'
/** Brand variant used for entity recognition (targets the "Laplace AI" query). */
export const BRAND_ALTERNATE_NAME = 'Laplace AI'
export const SUPPORT_EMAIL = 'support@laplace-gtm.com'
/** Square brand mark for schema.org `logo`; see scripts/og/render-icons.mjs. */
export const LOGO_IMAGE = `${SITE_URL}/icon-512.png`
/**
 * Public pricing for structured data. Mirrors GTM_PRICE_PER_EMAIL_USD and
 * GTM_CHECKOUT_AMOUNT.minUsd (a test keeps them equal); literal here so the
 * prerender script can import this file without the app's aliases.
 */
export const PRICE_PER_EMAIL_USD = '0.05'
export const MIN_TOP_UP_USD = 50
/** What the product does, for schema.org `featureList` and agents. */
const SOFTWARE_FEATURES = [
  'Researches your company, offer and buyers from your website',
  'Finds competitors and the companies that buy from them',
  'Ranks campaigns by budget share',
  'Finds real companies and named decision makers with verified emails',
  'Drafts a personalized first email for each person',
  'Sends from pre-warmed mailboxes assigned to your account, never your own domain',
  'Sorts replies and drafts answers; you approve every email',
] as const
/**
 * Official, public brand profiles for schema.org `sameAs`. Add real URLs
 * (LinkedIn company page, X, Crunchbase, GitHub org) as they go live — never
 * list a 404/private page here, a broken sameAs hurts entity signals.
 */
export const SOCIAL_PROFILES: ReadonlyArray<string> = []

export type PublicRouteKey =
  | 'landing'
  | 'terms'
  | 'privacy'
  | 'refund'
  | 'trust'
  | 'demo'
  | 'pricing'
  | 'integrations'
  | 'solutions'
  | 'security'

const ALL_PUBLIC_ROUTE_KEYS: ReadonlyArray<PublicRouteKey> = [
  'landing',
  'terms',
  'privacy',
  'refund',
  'trust',
  'demo',
  'pricing',
  'integrations',
  'solutions',
  'security',
]

/** Public routes that are live: prerendered, in the sitemap, in hreflang. */
export const PUBLIC_ROUTE_KEYS: ReadonlyArray<PublicRouteKey> =
  ALL_PUBLIC_ROUTE_KEYS.filter((key) => !isPublicPageHidden(key))

type LocalizedText = Record<SupportedLanguage, string>

interface PublicRouteConfig {
  /** Path appended after the `/{lang}` prefix. Empty for the landing page. */
  readonly segment: string
  readonly title: LocalizedText
  readonly description: LocalizedText
}

export const PUBLIC_ROUTES: Record<PublicRouteKey, PublicRouteConfig> = {
  landing: {
    segment: '',
    title: {
      ru: 'Laplace x GTM — AI SDR для B2B-продаж',
      en: 'Laplace x GTM — AI SDR for B2B sales',
    },
    description: {
      ru: 'Укажите сайт — через две минуты увидите реальных B2B-покупателей, тех, кто принимает решения, и первые письма им. $0,05 за отправленное письмо, без подписки.',
      en: 'Enter your website and see real B2B buyers, their decision makers and a personalized first email to each in two minutes. $0.05 per email sent, no subscription.',
    },
  },
  terms: {
    segment: '/terms',
    title: {
      ru: 'Условия использования — Laplace x GTM',
      en: 'Terms of Service — Laplace x GTM',
    },
    description: {
      ru: 'Условия использования Laplace x GTM (на английском): аккаунты, предоплаченные кредиты, отправка писем от вашего имени, допустимое использование и ответственность.',
      en: 'Terms of Service for Laplace x GTM, the AI SDR: accounts, prepaid credits, sending on your behalf, acceptable use and liability.',
    },
  },
  privacy: {
    segment: '/privacy',
    title: {
      ru: 'Политика конфиденциальности — Laplace x GTM',
      en: 'Privacy Policy — Laplace x GTM',
    },
    description: {
      ru: 'Политика конфиденциальности Laplace x GTM (на английском): данные клиентов и найденных деловых контактов, отказ от писем и удаление данных.',
      en: 'How Laplace x GTM handles information about customers and about the business contacts it finds, and how to opt out or delete your data.',
    },
  },
  refund: {
    segment: '/refund-policy',
    title: {
      ru: 'Политика возврата — Laplace x GTM',
      en: 'Refund Policy — Laplace x GTM',
    },
    description: {
      ru: 'Политика возврата Laplace x GTM (на английском) для предоплаченных кредитов; платежи обрабатывает Paddle.',
      en: 'Laplace x GTM refund policy for prepaid usage credits; payments are processed by Paddle.',
    },
  },
  trust: {
    segment: '/trust',
    title: {
      ru: 'Пакет доверия — Laplace',
      en: 'Trust package — Laplace',
    },
    description: {
      ru: 'Пакет доверия Laplace: приватность, обработка данных, подключённые источники, embeddings, субпроцессоры, cookies и удаление данных.',
      en: 'Laplace trust package: privacy, data processing, connected sources, embeddings, subprocessors, cookies and data deletion.',
    },
  },
  demo: {
    segment: '/demo',
    title: {
      ru: 'Запросить демо — Laplace',
      en: 'Request a demo — Laplace',
    },
    description: {
      ru: 'Запросите демо Laplace: расскажите о команде, источниках знаний и задачах, а мы покажем продукт на ваших сценариях.',
      en: 'Request a Laplace demo: share your team, knowledge sources and use cases, and we’ll show the product on your scenarios.',
    },
  },
  pricing: {
    segment: '/pricing',
    title: {
      ru: 'Тарифы Free, Team и Enterprise — Laplace',
      en: 'Free, Team and Enterprise pricing — Laplace',
    },
    description: {
      ru: 'Тарифы Laplace для самостоятельного старта, внедрения первого измеримого процесса и корпоративного масштаба с поставкой Cloud, Hybrid или On-Premises.',
      en: 'Laplace pricing for self-serve adoption, the first measurable workflow and organization-wide scale with Cloud, Hybrid or On-Premises delivery.',
    },
  },
  integrations: {
    segment: '/integrations',
    title: {
      ru: 'Интеграции — Laplace',
      en: 'Integrations — Laplace',
    },
    description: {
      ru: 'Интеграции Laplace: Jira, Confluence, GitHub, Gmail, Yandex Mail, Google Calendar и Яндекс Календарь доступны сейчас; остальные источники добавляются по мере развития продукта.',
      en: 'Laplace integrations: Jira, Confluence, GitHub, Gmail, Yandex Mail, Google Calendar and Yandex Calendar are available now, with more sources added as the product evolves.',
    },
  },
  solutions: {
    segment: '/solutions',
    title: {
      ru: 'Цифровые сотрудники для команд — Laplace',
      en: 'Digital employees for teams — Laplace',
    },
    description: {
      ru: 'Создавайте управляемых цифровых сотрудников для продаж, поддержки, маркетинга, разработки, аналитики, IT, юридических и HR-процессов.',
      en: 'Create human-controlled digital employees for Sales, Support, Marketing, Engineering, Data, IT, Legal and People workflows.',
    },
  },
  security: {
    segment: '/security',
    title: {
      ru: 'Безопасность — Laplace',
      en: 'Security — Laplace',
    },
    description: {
      ru: 'Безопасность Laplace: контроль доступа, права подключённого account, проверяемые источники, журнал действий и варианты поставки для организаций.',
      en: 'Laplace security: access control, connected-account permissions, verifiable sources, action logs and flexible enterprise delivery options.',
    },
  },
}

export function resolveLang(value: unknown): SupportedLanguage {
  return isSupportedLanguage(value) ? value : DEFAULT_LANGUAGE
}

/** Languages public routes are live in: prerendered, in the sitemap and hreflang. */
export function routeLanguages(): ReadonlyArray<SupportedLanguage> {
  return LIVE_LANGUAGES
}

/** `lang` when it is live, otherwise the default language. */
export function liveLang(lang: SupportedLanguage): SupportedLanguage {
  return LIVE_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE
}

/** Link to a public route for a visitor reading the site in `lang`. */
export function publicRouteHref(key: PublicRouteKey, lang: unknown): string {
  return routePath(key, liveLang(resolveLang(lang)))
}

/** Absolute path (no origin) for a public route in the given language, e.g. `/ru/terms`. */
export function routePath(
  key: PublicRouteKey,
  lang: SupportedLanguage,
): string {
  if (lang === DEFAULT_LANGUAGE) return PUBLIC_ROUTES[key].segment || '/'
  return `/${lang}${PUBLIC_ROUTES[key].segment}`
}

/** Absolute canonical URL for a public route in the given language. */
export function canonicalUrl(
  key: PublicRouteKey,
  lang: SupportedLanguage,
): string {
  return `${SITE_URL}${routePath(key, lang)}`
}

export type MetaTag =
  | { readonly title: string }
  | { readonly name: string; readonly content: string }
  | { readonly property: string; readonly content: string }

export interface LinkTag {
  readonly rel: string
  readonly href: string
  readonly hrefLang?: string
}

export interface ScriptTag {
  readonly type: string
  readonly children: string
}

export interface RouteHead {
  meta: Array<MetaTag>
  links: Array<LinkTag>
  scripts?: Array<ScriptTag>
}

interface BuildSeoHeadInput {
  readonly title: string
  readonly description: string
  readonly canonical: string
  readonly image?: string
  readonly ogType?: 'website' | 'article'
  readonly links?: ReadonlyArray<LinkTag>
  readonly meta?: ReadonlyArray<MetaTag>
  readonly scripts?: ReadonlyArray<ScriptTag>
}

/**
 * The title for link previews: previews already print og:site_name above
 * it, so the brand is dropped from "Laplace x GTM — …" / "… — Laplace x GTM".
 */
export function previewTitle(title: string): string {
  const prefix = `${SITE_NAME} — `
  const suffix = ` — ${SITE_NAME}`
  const bare = title.startsWith(prefix)
    ? title.slice(prefix.length)
    : title.endsWith(suffix)
      ? title.slice(0, -suffix.length)
      : title
  return bare ? bare.charAt(0).toUpperCase() + bare.slice(1) : title
}

/** Shared Open Graph, Twitter and canonical metadata for indexable pages. */
export function buildSeoHead({
  title,
  description,
  canonical,
  image = OG_IMAGE,
  ogType = 'website',
  links = [],
  meta = [],
  scripts,
}: BuildSeoHeadInput): RouteHead {
  return {
    meta: [
      { title },
      { name: 'description', content: description },
      { property: 'og:type', content: ogType },
      { property: 'og:site_name', content: SITE_NAME },
      { property: 'og:title', content: previewTitle(title) },
      { property: 'og:description', content: description },
      { property: 'og:url', content: canonical },
      { property: 'og:image', content: image },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: previewTitle(title) },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: image },
      ...meta,
    ],
    links: [{ rel: 'canonical', href: canonical }, ...links],
    ...(scripts ? { scripts: [...scripts] } : {}),
  }
}

function buildAlternateLinks(key: PublicRouteKey): Array<LinkTag> {
  const alternates: Array<LinkTag> = routeLanguages().map((lang) => ({
    rel: 'alternate',
    hrefLang: lang,
    href: canonicalUrl(key, lang),
  }))

  alternates.push({
    rel: 'alternate',
    hrefLang: 'x-default',
    href: canonicalUrl(key, DEFAULT_LANGUAGE),
  })

  return alternates
}

function buildLandingJsonLd(lang: SupportedLanguage): Array<ScriptTag> {
  const organization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    alternateName: BRAND_ALTERNATE_NAME,
    url: SITE_URL,
    logo: LOGO_IMAGE,
    email: SUPPORT_EMAIL,
    description: PUBLIC_ROUTES.landing.description[lang],
    ...(SOCIAL_PROFILES.length > 0 ? { sameAs: [...SOCIAL_PROFILES] } : {}),
  }

  const website = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    alternateName: BRAND_ALTERNATE_NAME,
    url: canonicalUrl('landing', lang),
    inLanguage: lang,
  }

  const software = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: PRODUCT_NAME,
    alternateName: BRAND_ALTERNATE_NAME,
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'AI sales development (AI SDR)',
    operatingSystem: 'Web',
    url: canonicalUrl('landing', lang),
    inLanguage: lang,
    description: PUBLIC_ROUTES.landing.description[lang],
    image: OG_IMAGE,
    featureList: [...SOFTWARE_FEATURES],
    publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    offers: [
      {
        '@type': 'Offer',
        name: 'Free preview',
        description:
          'Research, competitors, campaigns, companies, decision makers and first email drafts for your website. No signup or card.',
        price: '0',
        priceCurrency: 'USD',
      },
      {
        '@type': 'Offer',
        name: 'Email credits',
        description: `Prepaid usage credits from $${MIN_TOP_UP_USD}, no subscription. Lead search, research and drafts are included; credits are used only when an email is sent.`,
        price: PRICE_PER_EMAIL_USD,
        priceCurrency: 'USD',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: PRICE_PER_EMAIL_USD,
          priceCurrency: 'USD',
          unitText: 'email sent',
        },
      },
    ],
  }

  return [
    { type: 'application/ld+json', children: JSON.stringify(organization) },
    { type: 'application/ld+json', children: JSON.stringify(website) },
    { type: 'application/ld+json', children: JSON.stringify(software) },
  ]
}

/**
 * Localized, deduplicatable head for a public marketing route. Consumed by the
 * route-level `head` option + `<HeadContent />`; the prerender snapshot turns
 * the resulting tags into static source HTML.
 */
export function buildHead(key: PublicRouteKey, langInput: unknown): RouteHead {
  const lang = liveLang(resolveLang(langInput))
  const route = PUBLIC_ROUTES[key]
  const title = route.title[lang]
  const description = route.description[lang]
  const canonical = canonicalUrl(key, lang)

  return buildSeoHead({
    title,
    description,
    canonical,
    links: buildAlternateLinks(key),
    ...(key === 'landing' ? { scripts: buildLandingJsonLd(lang) } : {}),
  })
}

/** Site-wide defaults applied to every route (overridden by public-route heads). */
export const DEFAULT_HEAD: RouteHead = {
  meta: [
    { title: PRODUCT_NAME },
    {
      name: 'description',
      content:
        'Laplace x GTM is an AI sales employee: enter your website and see real buyers with first emails in two minutes.',
    },
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:image', content: OG_IMAGE },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:image', content: OG_IMAGE },
  ],
  links: [],
}
